import { Check, ChevronsUpDown, Plus, Settings2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useGroupsStore } from '@/features/groups/store';
import { cn } from '@/lib/cn';

/** Sidebar control for seeing and changing the open group. */
export function GroupSwitcher() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const groups = useGroupsStore((state) => state.groups);
  const summaries = useGroupsStore((state) => state.summaries);
  const activeGroupId = useGroupsStore((state) => state.activeGroupId);
  const setActiveGroup = useGroupsStore((state) => state.setActiveGroup);

  const active = groups.find((group) => group.id === activeGroupId) ?? null;
  const selectable = groups.filter((group) => !group.archived);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function go(path: string) {
    setOpen(false);
    navigate(path);
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex w-full items-center gap-3 rounded-2xl border border-line bg-elevated/50 p-2.5 text-left transition hover:bg-elevated"
      >
        <span
          className="inline-flex size-9 shrink-0 items-center justify-center rounded-xl text-[13px] font-bold text-white"
          style={{ backgroundColor: active?.color ?? 'var(--muted)' }}
          aria-hidden
        >
          {active?.name.trim().charAt(0).toUpperCase() ?? '?'}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold">
            {active?.name ?? 'No group'}
          </span>
          <span className="block truncate text-[11px] text-muted">
            {active
              ? `${summaries[active.id]?.memberCount ?? 0} members · ${active.currency}`
              : 'Create one to begin'}
          </span>
        </span>
        <ChevronsUpDown size={14} className="shrink-0 text-muted" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 right-0 top-full z-40 mt-2 animate-pop-in overflow-hidden rounded-2xl border border-line bg-surface shadow-pop"
        >
          <div className="max-h-72 overflow-y-auto scroll-thin p-1">
            {selectable.map((group) => (
              <button
                key={group.id}
                type="button"
                role="menuitem"
                onClick={() => {
                  setActiveGroup(group.id);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition hover:bg-elevated',
                  group.id === activeGroupId && 'bg-primary/[0.06] font-semibold'
                )}
              >
                <span
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: group.color }}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate text-[13px]">{group.name}</span>
                {group.id === activeGroupId && (
                  <Check size={14} className="shrink-0 text-primary" />
                )}
              </button>
            ))}

            {selectable.length === 0 && (
              <p className="px-3 py-2.5 text-[13px] text-muted">No groups yet.</p>
            )}
          </div>

          <div className="border-t border-line p-1">
            <button
              type="button"
              role="menuitem"
              onClick={() => go('/groups?new=1')}
              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] transition hover:bg-elevated"
            >
              <Plus size={14} className="text-muted" />
              New group
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => go('/groups')}
              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] transition hover:bg-elevated"
            >
              <Settings2 size={14} className="text-muted" />
              Manage groups
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
